/**
 * Visual constants shared between the printed hazardous-waste container
 * label (apps/lambdas/pdf-generator's `hazardousWasteLabel*` generators) and
 * its on-screen live preview (apps/oak's `LabelPreview`) -- the two must
 * never disagree on header copy, palette, or the core proportions that drive
 * layout, since the preview's whole job is to show what will actually
 * print. See `hazardClassDiamond.ts` next to this file for the class-diamond
 * half of that same contract.
 *
 * Only the MECHANICAL bits live here: literal strings, hex colors, the
 * handful of reference-scale numbers that drive both renderers' proportion
 * math, and the tiny copy helpers (classification chip text, the DOT
 * packing-group clause) whose wording/suppression rules must match. Each
 * renderer keeps its own unit system (PDF points vs. CSS pixels) and its own
 * full metrics/layout code -- this module is the single source of truth for
 * the values that must match, not a shared layout engine.
 *
 * pdf-generator's `hazardousWasteLabelStyles.ts` multiplies these by its own
 * `scale` factor exactly as it did with inline literals before this module
 * existed, so its rendered output is unchanged (verified by its own test
 * suite staying green). oak's `LabelPreview` applies the same numbers as
 * ratios of its fixed-pixel label width.
 */

import {
  PackingGroup,
  UniversalWasteCategory,
  WasteClassification,
} from "encamp-prisma/generated/browser";

import { safeParseInt } from "../utils/safeParseNumber";

/**
 * Human text for the CONTENTS field's classification chip, keyed by the
 * Prisma `WasteClassification` values. oak's GraphQL codegen enum has
 * different member NAMES (`RcraHazardous` vs `RCRA_HAZARDOUS`) but identical
 * runtime string values, so its members index this record directly -- no
 * accessor or cast needed.
 */
export const WASTE_CLASSIFICATION_LABELS: Record<WasteClassification, string> = {
  [WasteClassification.RCRA_ACUTELY_HAZARDOUS]: "RCRA acutely hazardous",
  [WasteClassification.RCRA_HAZARDOUS]: "RCRA hazardous",
  [WasteClassification.STATE_REGULATED]: "State regulated",
  [WasteClassification.NONHAZARDOUS]: "Non-hazardous",
};

/**
 * The "PG X" fragment of the DOT secondary line ("Class 3 Flammable Liquid ·
 * PG II") -- or undefined when the packing group is unset or NONE. NONE means
 * "no packing group assigned", not a marking, so printing "PG NONE" would put
 * a meaningless DOT clause on the label. Both renderers must suppress it the
 * same way, which is why the predicate lives here.
 *
 * Takes the Prisma enum; oak's GraphQL codegen `PackingGroup` members carry
 * the same runtime string values and are assignable directly.
 */
export function packingGroupClause(
  packingGroup: PackingGroup | null | undefined
): string | undefined {
  if (!packingGroup || packingGroup === PackingGroup.NONE) {
    return undefined;
  }
  return `PG ${packingGroup}`;
}

/** The reference 4x6 label canvas width, in the units each renderer scales
 * its own metrics from (PDF points for pdf-generator; CSS pixels for oak,
 * which renders its fixed-size 4x6 preview at this same numeric width). */
export const LABEL_REFERENCE_WIDTH = 512;

/** The RCRA hazardous-waste marking (40 CFR 262.32) -- the default, and what
 * `labelHeaderFor` returns for hazardous streams. Prefer that function: the
 * required marking differs by waste kind, and this constant on its own is
 * what put "HAZARDOUS WASTE" on used-oil drums. */
export const LABEL_HEADER_TITLE = "HAZARDOUS WASTE";

/** The 40 CFR 262.32 legend the printed label's header carries, split into
 * the bold lead sentence and a smaller regulatory remainder -- never
 * shortened, just visually separated (see LabelHeader in
 * hazardousWasteLabelComponents.tsx and its oak mirror). */
export const FEDERAL_WARNING_LEAD = "FEDERAL LAW PROHIBITS IMPROPER DISPOSAL.";
export const FEDERAL_WARNING_REMAINDER =
  "IF FOUND, CONTACT THE NEAREST POLICE OR PUBLIC SAFETY AUTHORITY OR THE U.S. ENVIRONMENTAL " +
  "PROTECTION AGENCY.";

/** The marking a container carries, chosen by waste kind -- see
 * `labelHeaderFor`. The federal legend rides along only where it applies. */
export interface LabelHeaderContent {
  title: string;
  /** Absent where the 262.32 legend does not apply (used oil, universal
   * waste, plain non-hazardous), so those labels do not claim a
   * hazardous-waste restriction they are not under. */
  warningLead?: string;
  warningRemainder?: string;
}

/**
 * The universal-waste marking phrase for each category.
 *
 * 40 CFR 273.14 does not permit a bare "Universal Waste" marking: it opens
 * "A small quantity handler of universal waste must label or mark the
 * universal waste TO IDENTIFY THE TYPE of universal waste as specified
 * below", and every paragraph then requires the type in the phrase --
 * (e) lamps must read "Universal Waste-Lamp(s)", "Waste Lamp(s)" or "Used
 * Lamp(s)". Naming the stream under CONTENTS does not satisfy it; the rule
 * wants one of the enumerated phrases.
 *
 * The federal five (273.9) use the regulation's own wording. The rest are
 * state-added categories with no federal phrase, so they follow the same
 * "UNIVERSAL WASTE - <type>" shape rather than inventing per-state wording.
 *
 * Uppercase to match every other marking this module produces; the em-dash
 * separator is spelled out as a hyphen because the label's Helvetica base-14
 * face has no reliable em-dash glyph in react-pdf.
 */
export const UW_CATEGORY_MARKING_TYPE: Record<UniversalWasteCategory, string> = {
  BATTERIES: "BATTERY(IES)",
  PESTICIDES: "PESTICIDE(S)",
  MERCURY_EQUIPMENT: "MERCURY CONTAINING EQUIPMENT",
  LAMPS: "LAMP(S)",
  AEROSOL_CANS: "AEROSOL CAN(S)",
  ELECTRONIC_WASTE: "ELECTRONIC WASTE",
  CATHODE_RAY_TUBES: "CATHODE RAY TUBE(S)",
  ANTIFREEZE: "ANTIFREEZE",
  PAINT: "PAINT",
  PHOTOVOLTAIC: "PHOTOVOLTAIC MODULE(S)",
  DENTAL_AMALGAM: "DENTAL AMALGAM",
  COMPRESSED_GAS: "COMPRESSED GAS",
  PCB_BALLASTS: "PCB BALLAST(S)",
  PHOTO_SOLUTIONS: "PHOTOGRAPHIC SOLUTION(S)",
};

/**
 * The universal-waste marking, typed where the category is known.
 *
 * Falls back to the bare marking when it is not. That fallback is NOT a
 * compliant marking -- see UW_CATEGORY_MARKING_TYPE -- so the label surface
 * warns the user to add the type rather than letting it print silently. The
 * fallback exists because the category is not carried on the waste stream the
 * label reads: it lives on the waste lines, which a container being
 * accumulated may not have yet.
 */
export function universalWasteMarking(category: UniversalWasteCategory | null | undefined): string {
  if (category == null) return "UNIVERSAL WASTE";
  return `UNIVERSAL WASTE - ${UW_CATEGORY_MARKING_TYPE[category]}`;
}

/**
 * The RCRA characteristic each characteristic waste code names.
 *
 * D001-D003 name themselves; D004-D043 are the toxicity codes, which all
 * indicate the same characteristic and so collapse to one word.
 */
const CHARACTERISTIC_BY_WASTE_CODE: Record<string, string> = {
  D001: "Ignitable",
  D002: "Corrosive",
  D003: "Reactive",
};
const TOXICITY_CHARACTERISTIC = "Toxic";
/** Printed in this order whatever order the codes arrive in. */
const CHARACTERISTIC_ORDER = ["Ignitable", "Corrosive", "Reactive", TOXICITY_CHARACTERISTIC];

function isToxicityWasteCode(code: string): boolean {
  if (!/^D\d{3}$/.test(code)) return false;
  const number = safeParseInt(code.slice(1));
  return number !== null && number >= 4 && number <= 43;
}

/**
 * The hazardous-waste characteristics the D-code (characteristic-waste) codes
 * in a set of federal waste codes name, in the fixed order above. Listed
 * waste codes (F/K/P/U) are read separately, by `listedWasteHazardsFor` in
 * `listedWasteHazards.ts`. Anything else this function does not recognise --
 * a genuinely unknown or state-specific code -- contributes nothing: it
 * carries no characteristic to name, and inventing one would put a hazard on
 * the drum that the code does not support.
 */
export function hazardCharacteristics(federalWasteCodes: string[]): string[] {
  const named = new Set<string>();
  for (const rawCode of federalWasteCodes) {
    const code = rawCode.trim().toUpperCase();
    const characteristic = CHARACTERISTIC_BY_WASTE_CODE[code];
    if (characteristic) {
      named.add(characteristic);
    } else if (isToxicityWasteCode(code)) {
      named.add(TOXICITY_CHARACTERISTIC);
    }
  }
  return CHARACTERISTIC_ORDER.filter((characteristic) => named.has(characteristic));
}

/** The DOT profile fields that decide whether the label's DOT block renders. */
export interface DotBlockContent {
  dotProperShippingName?: string | null;
  dotIdNumber?: string | null;
  dotHazardClass?: string | null;
}

/**
 * Whether the stream carries enough of a DOT profile to render the block --
 * and with it the hazard-class diamond, which is the label's primary
 * indication of the hazards of the contents.
 */
export function hasDotBlock(content: DotBlockContent): boolean {
  return Boolean(content.dotProperShippingName || content.dotIdNumber || content.dotHazardClass);
}

/**
 * The marking for a container, by the waste kind its stream carries.
 *
 * The required wording differs by kind and getting it wrong is itself a
 * marking violation -- a used-oil drum marked "HAZARDOUS WASTE" breaches
 * 40 CFR 279.22(c)(1) and invites handling non-hazardous waste as hazardous.
 *
 * - `USED_OIL` -- 40 CFR 279.22(c)(1) requires the words "Used Oil".
 * - `UNIVERSAL_WASTE` -- 40 CFR 273.14 requires the marking to identify the
 *   TYPE ("Universal Waste - Lamp(s)"), so pass the category and it is named.
 *   Without one the marking is the bare fallback, which is NOT compliant --
 *   the category lives on `WasteLineUnified`, not `WasteStream`, so a
 *   container accumulating before its first shipment has none to read. The
 *   label surface warns in that case instead of printing it silently.
 * - `PHARMACEUTICAL` -- Subpart P (40 CFR 266.502) marks non-creditable
 *   hazardous waste pharmaceuticals as such; they remain hazardous waste, so
 *   the federal legend still applies.
 * - `RCRA` / no kind -- classification decides: hazardous streams keep the
 *   262.32 marking, state-regulated waste says so rather than claiming to be
 *   non-hazardous, and a stream classified non-hazardous is not hazardous
 *   waste and must not say that it is.
 * - Neither kind nor classification known (a container with no waste stream
 *   assigned) -- the conservative 262.32 marking, NOT "NON-HAZARDOUS WASTE":
 *   that would be a compliance claim asserted from missing data. The label
 *   surface warns instead of presenting the fallback as an answer.
 */
export function labelHeaderFor(
  wasteKind: "RCRA" | "UNIVERSAL_WASTE" | "USED_OIL" | "PHARMACEUTICAL" | null | undefined,
  classification: WasteClassification | null | undefined,
  universalWasteCategory?: UniversalWasteCategory | null
): LabelHeaderContent {
  if (wasteKind === "USED_OIL") {
    return { title: "USED OIL" };
  }
  if (wasteKind === "UNIVERSAL_WASTE") {
    return { title: universalWasteMarking(universalWasteCategory) };
  }

  const isHazardous =
    classification === WasteClassification.RCRA_HAZARDOUS ||
    classification === WasteClassification.RCRA_ACUTELY_HAZARDOUS;

  if (wasteKind === "PHARMACEUTICAL") {
    return {
      title: "HAZARDOUS WASTE PHARMACEUTICALS",
      warningLead: FEDERAL_WARNING_LEAD,
      warningRemainder: FEDERAL_WARNING_REMAINDER,
    };
  }
  if (isHazardous) {
    return {
      title: LABEL_HEADER_TITLE,
      warningLead: FEDERAL_WARNING_LEAD,
      warningRemainder: FEDERAL_WARNING_REMAINDER,
    };
  }
  // State-regulated waste is regulated, just not federally RCRA-hazardous.
  // Marking it "NON-HAZARDOUS WASTE" would be its own false claim, in the
  // opposite direction to the one this function exists to fix.
  if (classification === WasteClassification.STATE_REGULATED) {
    return { title: "STATE REGULATED WASTE" };
  }
  if (classification === WasteClassification.NONHAZARDOUS) {
    return { title: "NON-HAZARDOUS WASTE" };
  }
  // Nothing known. `WasteStream.classification` is required, so this is the
  // container that carries no stream at all (`WasteContainer.wasteStreamId`
  // is nullable) -- not a stream that forgot to classify itself. Printing
  // "NON-HAZARDOUS WASTE" for it would be an affirmative compliance claim
  // made from absent data. Over-marking costs stricter handling;
  // under-marking a drum that turns out hazardous is the violation, so
  // absence falls back to the conservative marking and the label surface
  // warns that nothing was read.
  return {
    title: LABEL_HEADER_TITLE,
    warningLead: FEDERAL_WARNING_LEAD,
    warningRemainder: FEDERAL_WARNING_REMAINDER,
  };
}

/** Helvetica-Bold advance widths (per 1000 em) for the glyphs a header title
 * can contain. Exact rather than the average-width estimate the left-column
 * fit uses: the title box is a FIXED width chosen so the marking never wraps
 * onto a second line, so an under-estimate here would break that guarantee
 * outright rather than just crowd a field. */
const HELVETICA_BOLD_TITLE_WIDTHS: Record<string, number> = {
  " ": 278,
  "-": 333,
  "(": 333,
  ")": 333,
  ".": 278,
  "/": 278,
  A: 722,
  B: 722,
  C: 722,
  D: 722,
  E: 667,
  F: 611,
  G: 778,
  H: 722,
  I: 278,
  J: 556,
  K: 722,
  L: 611,
  M: 833,
  N: 722,
  O: 778,
  P: 667,
  Q: 778,
  R: 722,
  S: 667,
  T: 611,
  U: 722,
  V: 667,
  W: 944,
  X: 667,
  Y: 667,
  Z: 611,
  "0": 556,
  "1": 556,
  "2": 556,
  "3": 556,
  "4": 556,
  "5": 556,
  "6": 556,
  "7": 556,
  "8": 556,
  "9": 556,
};
/** Widest glyph above, so an unexpected character over-reserves rather than
 * silently under-reserving and letting the title wrap. */
const HELVETICA_BOLD_TITLE_FALLBACK_WIDTH = 944;

/** The title's width in points at a given font size. */
export function headerTitleWidthAt(title: string, fontSize: number): number {
  const ems = [...title].reduce(
    (total, char) =>
      total + (HELVETICA_BOLD_TITLE_WIDTHS[char] ?? HELVETICA_BOLD_TITLE_FALLBACK_WIDTH),
    0
  );
  return (ems / 1000) * fontSize;
}

/**
 * The largest font size at or below `maxFontSize` that keeps `title` on one
 * line inside `boxWidth`.
 *
 * "HAZARDOUS WASTE" was sized to fill the box almost exactly (measured 187.5pt
 * of 195pt at the reference scale), so every longer marking overflows it --
 * "NON-HAZARDOUS WASTE" by ~43pt and "HAZARDOUS WASTE PHARMACEUTICALS" by
 * roughly double. Shrinking the longer ones is what lets the required wording
 * be the required wording instead of an abbreviation chosen to fit a box.
 */
export function fitHeaderTitleSize(title: string, boxWidth: number, maxFontSize: number): number {
  const naturalWidth = headerTitleWidthAt(title, maxFontSize);
  if (naturalWidth <= boxWidth) return maxFontSize;
  return maxFontSize * (boxWidth / naturalWidth);
}

/** Whether this header carries the 40 CFR 262.32 legend beside the marking.
 * Both renderers key their header layout off this, so they agree on when the
 * marking is sharing the row and when it owns it. */
export function headerCarriesLegend(header: LabelHeaderContent): boolean {
  return Boolean(header.warningLead && header.warningRemainder);
}

/**
 * The width `fitHeaderTitleSize` solves the marking against.
 *
 * With the legend the marking keeps the narrow left-hand box, which is sized
 * so it can never wrap. Without it the marking is the header's only content:
 * it takes the whole row and centres, so it also has the right column's width
 * to spend and needs less shrinking.
 *
 * Shared rather than decided per renderer -- when only one of them widened
 * the box, every no-legend marking ("NON-HAZARDOUS WASTE", "STATE REGULATED
 * WASTE", the typed universal-waste markings) previewed at a size it would
 * not print at.
 */
export function headerTitleBoxWidth(
  header: LabelHeaderContent,
  headerTitleWidth: number,
  rightColumnWidth: number
): number {
  return headerCarriesLegend(header) ? headerTitleWidth : headerTitleWidth + rightColumnWidth;
}

export const HEADER_COLOR = "#FF6B35";
export const HEADER_TEXT_COLOR = "#FFFFFF";
export const MUTED_TEXT_COLOR = "#6B7280";
export const VALUE_TEXT_COLOR = "#263357";
export const SECONDARY_TEXT_COLOR = "#374151";
/** @deprecated The single purple chip, kept only so nothing outside the label
 * breaks on the symbol. Use `classificationChip`, which carries the
 * classification's own colour. */
export const CHIP_BACKGROUND_COLOR = "#F1EAFB";
/** @deprecated See CHIP_BACKGROUND_COLOR. */
export const CHIP_TEXT_COLOR = "#7C3AED";

/** How the CONTENTS chip renders for one classification. */
export interface ClassificationChip {
  text: string;
  backgroundColor: string;
  textColor: string;
}

/**
 * The CONTENTS chip: the classification's wording and its colour.
 *
 * Both are here rather than at the two render sites because the printed label
 * and the on-screen preview must agree, and because the pair only makes sense
 * together -- the whole point of the colour is to reinforce the word.
 *
 * Colours follow the design system's chip spec: the semantic palette
 * (error/warning/info) as a light 8% fill with the darker shade for text,
 * never a solid fill. A single hue for every classification -- which is what
 * the label shipped with -- means an acutely hazardous drum and a
 * non-hazardous one carry visually identical chips, on the one artifact where
 * that distinction matters most.
 *
 * Uppercase because this chip lives on a printed label whose every other
 * field label is uppercase, and because "Non-hazardous" set in sentence case
 * puts the single word that changes the drum's handling in lower case.
 * `WASTE_CLASSIFICATION_LABELS` keeps the sentence-case wording for any
 * non-label reader.
 */
export function classificationChip(classification: WasteClassification): ClassificationChip {
  const text = WASTE_CLASSIFICATION_LABELS[classification].toUpperCase();
  switch (classification) {
    // Both RCRA classifications are hazardous waste; the wording separates
    // acute from non-acute, so the colour does not need to.
    case WasteClassification.RCRA_ACUTELY_HAZARDOUS:
    case WasteClassification.RCRA_HAZARDOUS:
      return { text, backgroundColor: "#FCEEEE", textColor: "#C62828" };
    case WasteClassification.STATE_REGULATED:
      return { text, backgroundColor: "#FEF3EB", textColor: "#E65100" };
    case WasteClassification.NONHAZARDOUS:
      return { text, backgroundColor: "#EBF6FB", textColor: "#01579B" };
  }
}
export const RULE_COLOR = "#D1D5DB";

/**
 * Reference-scale (i.e. at `LABEL_REFERENCE_WIDTH`) point/pixel sizes for the
 * handful of metrics both renderers need to agree on. pdf-generator's
 * `labelMetrics(pageWidth)` multiplies each by `pageWidth / LABEL_REFERENCE_WIDTH`
 * -- exactly the arithmetic it already did with these as inline literals, so
 * moving them here changes nothing about its output. oak's `LabelPreview`
 * (which only ever renders at `LABEL_REFERENCE_WIDTH` for its 4x6/"sm" fixed
 * preview) can use these numbers directly as CSS pixel sizes.
 */
export const HEADER_PADDING_VERTICAL_AT_REFERENCE = 6;
export const HEADER_TITLE_SIZE_AT_REFERENCE = 18.5;
/** The header title's FIXED box width. Locked down (rather than sized by
 * intrinsic measurement) so the marking can never wrap onto a second line --
 * see LabelHeader in hazardousWasteLabelComponents.tsx. Shared because
 * `fitHeaderTitleSize` has to solve against the same box in both renderers. */
export const HEADER_TITLE_WIDTH_AT_REFERENCE = 195;
export const HEADER_WARNING_LEAD_SIZE_AT_REFERENCE = 6.8;
export const HEADER_WARNING_REMAINDER_SIZE_AT_REFERENCE = 5.5;
export const FIELD_LABEL_SIZE_AT_REFERENCE = 7.5;
/** `fieldLabelLetterSpacing` in pdf-generator's `labelMetrics` -- the small
 * caps field labels (GENERATOR, EPA ID NUMBER, ...) are letter-spaced, not
 * just sized down. */
export const FIELD_LABEL_LETTER_SPACING_AT_REFERENCE = 0.7;
export const VALUE_SIZE_AT_REFERENCE = 9.5;
export const DIAMOND_SIZE_AT_REFERENCE = 72;
export const QR_SIZE_AT_REFERENCE = 110;
/** `chipTextSize` -- the CONTENTS field's classification chip (e.g. "RCRA
 * hazardous"). Numerically equal to FIELD_LABEL_SIZE_AT_REFERENCE today, but
 * kept as its own named constant since pdf-generator tracks it as a
 * distinct metric that could diverge later. */
export const CHIP_TEXT_SIZE_AT_REFERENCE = 7.5;
/** `idShippingNameSize` -- the DOT ID + proper shipping name line under the
 * hazard diamond (e.g. "UN1993 · Waste Flammable liquid, n.o.s."). */
export const ID_SHIPPING_NAME_SIZE_AT_REFERENCE = 11;
/** `secondaryLineSize` -- the "Class X ... · PG Y" line under the DOT id/
 * shipping name. */
export const SECONDARY_LINE_SIZE_AT_REFERENCE = 9;
/** `diamondNumberSize` -- the hazard-class number rendered inside the
 * diamond itself. */
export const DIAMOND_NUMBER_SIZE_AT_REFERENCE = 16;
/** `captionSize` -- the container-label line under the QR (also used, at the
 * page's own scale, for the QR_ONLY layout's single page). The PDF has no
 * truncation/shrink logic for a long container label; it simply renders this
 * small enough that realistic container labels fit the right column's width
 * on one line -- mirror the size, not a truncation scheme that doesn't exist
 * on the printed label. */
export const CAPTION_SIZE_AT_REFERENCE = 7.5;

/**
 * The right column's (DOT block + QR) share of the label's total width.
 * pdf-generator applies this to the page width net of outer padding, the
 * column rule, and the gaps on both sides of it (see `labelMetrics`'s
 * `rightColumnWidth`); oak applies it directly to its fixed label width as a
 * close approximation of the same net-of-padding math (close enough that the
 * preview's column reads at the same width without reproducing the PDF's
 * exact padding arithmetic).
 */
export const RIGHT_COLUMN_WIDTH_RATIO = 0.37;

/**
 * The right column's width at reference scale, net of the outer padding, the
 * column rule, and the gaps on either side of it -- pdf-generator's
 * `labelMetrics` derives its `rightColumnWidth` as this times its own scale
 * factor, which is exactly the arithmetic it did inline before.
 *
 * Oak's body column applies RIGHT_COLUMN_WIDTH_RATIO to the label width
 * directly, an approximation it can afford (see the note above). The header
 * title's fit cannot: the box width there sets a font-size ratio, so the
 * ~8% the approximation is off by would land as an ~8% size difference
 * between preview and print on every no-legend marking.
 */
export const RIGHT_COLUMN_WIDTH_AT_REFERENCE =
  (LABEL_REFERENCE_WIDTH - 2 * 11 - 0.75 - 2 * 8) * RIGHT_COLUMN_WIDTH_RATIO;

/** `subsidiaryDiamondSize` -- the subsidiary hazard design, drawn smaller
 * than the primary. */
export const SUBSIDIARY_DIAMOND_SIZE_AT_REFERENCE = 44;
/** `subsidiaryDiamondNumberSize` -- the subsidiary's class number, kept in
 * the primary's number-to-diamond proportion. */
export const SUBSIDIARY_DIAMOND_NUMBER_SIZE_AT_REFERENCE =
  (DIAMOND_NUMBER_SIZE_AT_REFERENCE * SUBSIDIARY_DIAMOND_SIZE_AT_REFERENCE) /
  DIAMOND_SIZE_AT_REFERENCE;
/** `rcraPictogramSize` -- one symbol in the RCRA characteristic strip. */
export const RCRA_PICTOGRAM_SIZE_AT_REFERENCE = 26;
/** `orientationArrowsSize` -- the 49 CFR 172.312 package orientation mark. */
export const ORIENTATION_ARROWS_SIZE_AT_REFERENCE = 30;
/** `inhalationHazardSize` -- the 49 CFR 172.313(a) inhalation marking. */
export const INHALATION_HAZARD_SIZE_AT_REFERENCE = 8;

/** 49 CFR 172.313(a) prescribes the words "Inhalation Hazard", so the marking
 * is a constant rather than assembled copy. */
export const INHALATION_HAZARD_MARKING = "INHALATION HAZARD";

/**
 * The 172.313(a) marking, for division 2.3 only.
 *
 * Keys off the division, not the bare class: a "2" with no division is a
 * class whose division is unknown, and marking an unknown gas as poisonous by
 * inhalation is a claim made from absent data.
 */
export function poisonInhalationLine(
  dotHazardClass: string | null | undefined
): string | undefined {
  return dotHazardClass?.trim() === "2.3" ? INHALATION_HAZARD_MARKING : undefined;
}
